import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Flex,
  Icon,
  Badge,
  Card,
  CardBody,
  useColorMode,
  Divider,
  SimpleGrid,
  Button,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon,
  Tag,
  TagLabel,
  TagLeftIcon,
} from "@chakra-ui/react";
import {
  FaSeedling,
  FaFlask,
  FaUserShield,
  FaUsers,
  FaCity,
  FaCubes,
  FaGlobeAmericas,
  FaCheckCircle,
  FaSpinner,
  FaCircle,
  FaArrowRight,
  FaCoins,
  FaGraduationCap,
  FaCalendarAlt,
  FaLaptopCode,
  FaChartLine,
  FaHandshake,
  FaHeart,
  FaPaintBrush,
  FaChild,
  FaBuilding,
  FaBolt,
  FaShieldAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

interface TokenActivity {
  name: string;
  tokens: number;
}

interface Course {
  name: string;
  gated?: string;
}

interface CommunityEvent {
  name: string;
  frequency: string;
}

interface CommunityPhase {
  number: number;
  title: string;
  subtitle: string;
  philosophy: string;
  status: "completed" | "current" | "upcoming";
  icon: any;
  color: string;
  governanceSize: string;
  tokenActivities: TokenActivity[];
  courses: Course[];
  events: CommunityEvent[];
  techMilestones: string[];
  revenueMilestone: string;
  exitCriteria: string[];
}

const phases: CommunityPhase[] = [
  {
    number: 0,
    title: "Foundation",
    subtitle: "Build the Tools. Prove They Work.",
    philosophy:
      "Build the tools. Prove they work. Use them yourself.",
    status: "completed",
    icon: FaSeedling,
    color: "#6B7280",
    governanceSize: "Tom only",
    tokenActivities: [],
    courses: [],
    events: [],
    techMilestones: [
      "Multi-tenant GraphQL API",
      "48+ business modules built",
      "AI Voice Assistant (Vapi)",
      "Webinar system (Interest → Register → Live → Purchase)",
      "BB Token infrastructure",
      "Accountability & Wheel of Life coaching",
    ],
    revenueMilestone: "$15K-$25K/mo (client projects)",
    exitCriteria: [
      "First webinar executed with real attendees",
      "BB Token config live on TMS tenant",
      "At least 5 BB Tokens issued to real users",
      "First $97/month subscriber from webinar",
    ],
  },
  {
    number: 1,
    title: "Discovery & Tom's Lab",
    subtitle: "Experiment. Measure. Learn.",
    philosophy:
      "Tom experiments with what activities bring real value. The lab before the launch.",
    status: "current",
    icon: FaFlask,
    color: "#F59E0B",
    governanceSize: "Tom + first subscribers (5-15 people)",
    tokenActivities: [
      { name: "Register for webinar", tokens: 1 },
      { name: "Attend live webinar", tokens: 1 },
      { name: "Stay till end of webinar", tokens: 1 },
      { name: "Refer someone who registers", tokens: 1 },
      { name: "Post about BB on social media", tokens: 1 },
      { name: "Subscribe (sign-up bonus)", tokens: 2 },
      { name: "Each month as active subscriber", tokens: 1 },
      { name: "Complete a course in LMS", tokens: 2 },
      { name: "Record a testimonial video", tokens: 2 },
    ],
    courses: [
      { name: "Getting Started with Business Builder", gated: "Free for all subscribers" },
      { name: "Webinar Mastery 101", gated: "$97/mo subscription" },
    ],
    events: [
      { name: "Monthly virtual webinars", frequency: "Monthly" },
      { name: "Coffee with top 3 engaged subscribers (Brisbane)", frequency: "Quarterly" },
    ],
    techMilestones: [
      "BB Token issuance & tracking live",
      "Webinar auto-token rewards",
      "Token-to-bill discount system",
      "LMS course publishing",
      "Referral reward tracking",
    ],
    revenueMilestone: "$20K-$35K/mo",
    exitCriteria: [
      "3+ months of successful webinars",
      "15+ active subscribers",
      "3+ subscribers with 10+ tokens each",
      "5+ activities that consistently drive value identified",
    ],
  },
  {
    number: 2,
    title: "Inner Circle",
    subtitle: "Small Group. Deep Trust.",
    philosophy:
      "Invite the most committed contributors to help shape the system. Small group, deep trust.",
    status: "upcoming",
    icon: FaUserShield,
    color: "#8B5CF6",
    governanceSize: "5 governance members (invited by Tom)",
    tokenActivities: [
      { name: "Attend governance meeting", tokens: 2 },
      { name: "Submit agenda item (discussed)", tokens: 1 },
      { name: "Complete BB Governance 101", tokens: 3 },
      { name: "Mentor a new subscriber", tokens: 2 },
      { name: "Report a confirmed bug", tokens: 1 },
      { name: "Attend BB Builder Camp", tokens: 5 },
    ],
    courses: [
      { name: "BB Governance 101", gated: "15+ earned tokens" },
      { name: "Building Your Business with BB", gated: "Subscriber" },
    ],
    events: [
      { name: "First BB Builder Camp (1-day, Brisbane)", frequency: "Once" },
      { name: "Governance dinners (5 people, intimate)", frequency: "Quarterly" },
    ],
    techMilestones: [
      "CI Meeting module for governance",
      "Background check verification",
      "Governance meeting recordings",
      "Token activity dashboard",
    ],
    revenueMilestone: "$25K-$40K/mo",
    exitCriteria: [
      "3+ successful governance meetings",
      "2+ members consistently attending and contributing",
      "30+ active subscribers",
      "3+ physical events completed",
    ],
  },
  {
    number: 3,
    title: "Growing Council",
    subtitle: "The Community Teaches Itself.",
    philosophy:
      "The council grows. Sub-committees emerge. The community starts teaching itself.",
    status: "upcoming",
    icon: FaUsers,
    color: "#10B981",
    governanceSize: "15 governance members (majority vote)",
    tokenActivities: [
      { name: "Publish a course module (approved)", tokens: 5 },
      { name: "Run a local meetup (verified)", tokens: 3 },
      { name: "Complete accountability snapshot", tokens: 1 },
      { name: "Help another tenant migrate data", tokens: 2 },
      { name: "Propose a reward activity (adopted)", tokens: 2 },
      { name: "Bring parent/educator to express interest", tokens: 1 },
    ],
    courses: [
      { name: "Accountability Coaching Certification", gated: "$997 per cohort" },
      { name: "Token Economics for Your Business" },
      { name: "Community-submitted courses (approved by Content Committee)" },
    ],
    events: [
      { name: "Quarterly BB Builder Camps (2-day, themed)", frequency: "Quarterly" },
      { name: "Local meetups organized by governance members", frequency: "Monthly" },
    ],
    techMilestones: [
      "Sub-committee system (Content, Events, Standards)",
      "Quadratic voting on proposals",
      "Reputation score (soulbound, non-transferable)",
      "15% voting cap per individual",
    ],
    revenueMilestone: "$40K-$60K/mo — THE $60K TARGET",
    exitCriteria: [
      "100+ active subscribers",
      "5+ camps completed",
      "3+ community-submitted courses published",
      "Revenue at or above $60K/month",
      "At least 1 member ready to deploy their own BB instance",
    ],
  },
  {
    number: 4,
    title: "Community Scale",
    subtitle: "Tom Steps Back. New Leaders Emerge.",
    philosophy:
      "The community governs itself. Tom steps back. New leaders emerge.",
    status: "upcoming",
    icon: FaCity,
    color: "#3B82F6",
    governanceSize: "50-100 members (Core Council + Community Forum)",
    tokenActivities: [
      { name: "Deploy a white-label BB instance", tokens: 10 },
      { name: "Facilitate a webinar (not Tom)", tokens: 5 },
      { name: "Cross-tenant collaboration", tokens: 2 },
      { name: "Organize a community workshop", tokens: 3 },
      { name: "Contribute to open-source module", tokens: 5 },
    ],
    courses: [
      { name: "Deploying Your Own BB Instance", gated: "50+ earned tokens" },
      { name: "Community Building Mastery" },
      { name: "10+ community-authored courses" },
    ],
    events: [
      { name: "BB Builder Conference (Annual, 3-day)", frequency: "Annually" },
      { name: "Quarterly regional camps", frequency: "Quarterly" },
      { name: "Monthly local meetups in 5+ cities", frequency: "Monthly" },
      { name: "Governance Retreat (Core Council)", frequency: "Annually" },
    ],
    techMilestones: [
      "Two-tier governance (Core Council + Community Forum)",
      "White-label licensing system",
      "Cross-tenant marketplace",
      "Automated governance proposals",
    ],
    revenueMilestone: "$60K-$100K/mo",
    exitCriteria: [
      "200+ active subscribers",
      "5+ white-label BB instances deployed",
      "Core Council operating independently",
      "Annual conference completed",
    ],
  },
  {
    number: 5,
    title: "Reality Integration",
    subtitle: "Trust Becomes Verifiable.",
    philosophy:
      "Trust becomes verifiable. Governance becomes immutable. The economy goes on-chain.",
    status: "upcoming",
    icon: FaCubes,
    color: "#9333EA",
    governanceSize: "Global community (biometric = 1 human = 1 vote)",
    tokenActivities: [
      { name: "Run a Reality node", tokens: 5 },
      { name: "Verify governance votes via Reality Check", tokens: 1 },
      { name: "Participate in Sentiment rApp", tokens: 1 },
    ],
    courses: [
      { name: "Running a Reality Node" },
      { name: "Smart Contracts for Business" },
    ],
    events: [
      { name: "Reality Builder Summit", frequency: "Annually" },
      { name: "Node operator meetups", frequency: "Quarterly" },
    ],
    techMilestones: [
      "On-chain token registry",
      "Smart contract governance",
      "Biometric verification",
      "Automated profit distribution",
      "Cross-chain value transfer",
    ],
    revenueMilestone: "$100K+/mo",
    exitCriteria: [
      "On-chain governance operational",
      "NET + BB dual-token flywheel active",
      "BB Token tradeable on Reality exchange",
    ],
  },
  {
    number: 6,
    title: "Sovereignty",
    subtitle: "The Master Creates More Masters.",
    philosophy:
      "The master creates more masters. The sovereign community creates more sovereign communities.",
    status: "upcoming",
    icon: FaGlobeAmericas,
    color: "#EC4899",
    governanceSize: "Full DAO with automated proposal execution",
    tokenActivities: [
      { name: "Graduate from mastery program", tokens: 20 },
      { name: "Deployed instance reaches 10 subscribers", tokens: 10 },
      { name: "Teach others to deploy", tokens: 5 },
    ],
    courses: [
      { name: "Teaching Others to Deploy (mastery graduation)" },
    ],
    events: [
      { name: "International BB conferences", frequency: "Annually" },
      { name: "Cross-network summits", frequency: "Biannually" },
    ],
    techMilestones: [
      "Full DAO governance",
      "20-50 interconnected BB instances",
      "Self-hosted option available",
      "AI trained on YOUR data only",
      "Full data portability",
    ],
    revenueMilestone: "$200K+/mo (network of licensed instances)",
    exitCriteria: [
      "Network of 20+ BB instances running independently",
      "Tom is Founder Emeritus with advisory role",
      "Community is self-sustaining",
    ],
  },
];

const Journey = () => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.9) 50%, rgba(255,255,255,0.95) 100%)"
      : "linear-gradient(135deg, rgba(20,20,20,0.8) 0%, rgba(30,30,30,0.6) 50%, rgba(20,20,20,0.8) 100%)";
  const cardBorder =
    colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";
  const subtleBg =
    colorMode === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)";

  const getStatusIcon = (status: CommunityPhase["status"]) => {
    switch (status) {
      case "completed": return FaCheckCircle;
      case "current": return FaSpinner;
      case "upcoming": return FaCircle;
    }
  };

  const getStatusLabel = (status: CommunityPhase["status"]) => {
    switch (status) {
      case "completed": return "Completed";
      case "current": return "In Progress";
      case "upcoming": return "Coming Soon";
    }
  };

  const getStatusColorScheme = (status: CommunityPhase["status"]) => {
    switch (status) {
      case "completed": return "green";
      case "current": return "yellow";
      case "upcoming": return "gray";
    }
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="800px" py={16}>
        <VStack spacing={12} align="stretch">
          {/* Hero */}
          <Box textAlign="center" py={8}>
            <Badge
              bg="linear-gradient(135deg, #10B981 0%, #3B82F6 50%, #9333EA 100%)"
              color="white"
              fontSize="md"
              px={6}
              py={2}
              borderRadius="full"
              fontWeight="600"
              mb={6}
              boxShadow="0 4px 16px rgba(16, 185, 129, 0.4)"
            >
              The Conscious Economy
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="800"
              mb={4}
              color={textPrimary}
              bgGradient="linear(to-r, #10B981, #3B82F6, #9333EA)"
              bgClip="text"
            >
              From Builder to Sovereign Community
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={textSecondary} maxW="800px" mx="auto" mb={4}>
              We don't reward capital. We reward contribution that improves the world.
            </Text>
            <Text fontSize="md" color={textSecondary} fontStyle="italic">
              At every governance meeting, we ask one question: "Does this improve the world, or make it worse?"
            </Text>
          </Box>

          {/* Philosophy: Why We're Different */}
          <Card
            bg={cardGradientBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="2xl"
            overflow="hidden"
          >
            <CardBody p={0}>
              <Box
                bg="linear-gradient(135deg, #10B981 0%, #059669 100%)"
                px={8}
                py={5}
              >
                <Heading size="lg" color="white">
                  Why We're Different
                </Heading>
                <Text color="whiteAlpha.900" fontSize="sm" mt={1}>
                  Proof of Impact — not Proof of Capital
                </Text>
              </Box>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                {/* Traditional */}
                <Box p={6} borderRight={{ md: "1px solid" }} borderColor={cardBorder}>
                  <Flex align="center" gap={2} mb={4}>
                    <Box w={3} h={3} borderRadius="full" bg="red.400" />
                    <Text fontWeight="700" color="red.400" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                      Traditional Crypto
                    </Text>
                  </Flex>
                  <VStack align="start" spacing={2}>
                    {[
                      "Buy tokens with $$$",
                      "More $$$ = more tokens",
                      "More tokens = more power",
                      "More power = more rewards",
                      "Rich get richer",
                    ].map((item, i) => (
                      <Flex key={i} align="center" gap={2}>
                        {i < 4 ? (
                          <Icon as={FaArrowRight} color="red.300" boxSize={3} />
                        ) : (
                          <Icon as={FaBolt} color="red.400" boxSize={3} />
                        )}
                        <Text fontSize="sm" color={textSecondary}>
                          {item}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                {/* Conscious Economy */}
                <Box p={6}>
                  <Flex align="center" gap={2} mb={4}>
                    <Box w={3} h={3} borderRadius="full" bg="green.400" />
                    <Text fontWeight="700" color="green.400" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                      The Conscious Economy
                    </Text>
                  </Flex>
                  <VStack align="start" spacing={2}>
                    {[
                      "Contribute value to community",
                      "Community measures impact",
                      "Earn tokens through Proof of Impact",
                      "Governance reviews: did this improve the world?",
                      "Community grows together",
                    ].map((item, i) => (
                      <Flex key={i} align="center" gap={2}>
                        {i < 4 ? (
                          <Icon as={FaArrowRight} color="green.300" boxSize={3} />
                        ) : (
                          <Icon as={FaHeart} color="green.400" boxSize={3} />
                        )}
                        <Text fontSize="sm" color={textSecondary}>
                          {item}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </SimpleGrid>

              <Box px={8} pb={6}>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                  {[
                    { label: "Tokens earned", sub: "not bought" },
                    { label: "Quadratic voting", sub: "prevents whales" },
                    { label: "Background checks", sub: "trust verified" },
                    { label: "Real meetups", sub: "not just Discord" },
                  ].map((item, i) => (
                    <Box
                      key={i}
                      bg={subtleBg}
                      borderRadius="lg"
                      p={3}
                      textAlign="center"
                    >
                      <Text fontSize="xs" fontWeight="700" color="green.400">
                        {item.label}
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        {item.sub}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </CardBody>
          </Card>

          {/* 7-Phase Timeline */}
          <Box>
            <Heading size="xl" color={textPrimary} mb={2} textAlign="center">
              The 7-Phase Journey
            </Heading>
            <Text color={textSecondary} textAlign="center" mb={8}>
              From one builder to a sovereign global community
            </Text>

            <Accordion allowMultiple defaultIndex={[1]}>
              {phases.map((phase) => (
                <AccordionItem
                  key={phase.number}
                  border="none"
                  mb={4}
                >
                  <Card
                    bg={phase.status === "current"
                      ? `linear-gradient(135deg, ${phase.color}15 0%, ${phase.color}05 100%)`
                      : cardGradientBg}
                    borderWidth={phase.status === "current" ? "2px" : "1px"}
                    borderColor={phase.status === "current" ? `${phase.color}80` : cardBorder}
                    borderRadius="2xl"
                    boxShadow={phase.status === "current"
                      ? `0 8px 32px ${phase.color}30`
                      : "0 2px 8px rgba(0,0,0,0.1)"}
                    opacity={phase.status === "upcoming" ? 0.8 : 1}
                    transition="all 0.3s"
                    _hover={{ opacity: 1, transform: "translateY(-2px)" }}
                    overflow="hidden"
                  >
                    <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                      <CardBody p={6} w="full">
                        <Flex align="center" justify="space-between" w="full">
                          <Flex align="center" gap={4}>
                            <Flex
                              align="center"
                              justify="center"
                              w={12}
                              h={12}
                              borderRadius="xl"
                              bg={`${phase.color}20`}
                              flexShrink={0}
                            >
                              <Icon as={phase.icon} w={6} h={6} color={phase.color} />
                            </Flex>
                            <Box textAlign="left">
                              <Flex align="center" gap={2} mb={1} flexWrap="wrap">
                                <Badge colorScheme="purple" fontSize="xs">
                                  Phase {phase.number}
                                </Badge>
                                <Badge
                                  colorScheme={getStatusColorScheme(phase.status)}
                                  fontSize="xs"
                                  variant="solid"
                                >
                                  <Icon as={getStatusIcon(phase.status)} mr={1} />
                                  {getStatusLabel(phase.status)}
                                </Badge>
                                <Badge fontSize="xs" variant="outline" colorScheme="blue">
                                  {phase.governanceSize}
                                </Badge>
                              </Flex>
                              <Heading size="md" color={textPrimary}>
                                {phase.title}
                              </Heading>
                              <Text fontSize="sm" color={phase.color} fontWeight="600">
                                {phase.subtitle}
                              </Text>
                            </Box>
                          </Flex>
                          <Flex align="center" gap={3}>
                            <Box textAlign="right" display={{ base: "none", md: "block" }}>
                              <Text fontSize="xs" color={textSecondary}>Revenue</Text>
                              <Text fontSize="sm" fontWeight="700" color={phase.color}>
                                {phase.revenueMilestone.split("(")[0].trim().replace(" — THE $60K TARGET", "")}
                              </Text>
                            </Box>
                            <AccordionIcon color={textSecondary} />
                          </Flex>
                        </Flex>
                      </CardBody>
                    </AccordionButton>

                    <AccordionPanel pb={6} px={6} pt={0}>
                      <Divider mb={4} />

                      {/* Philosophy */}
                      <Text
                        fontSize="md"
                        color={textSecondary}
                        fontStyle="italic"
                        mb={5}
                        pl={4}
                        borderLeft="3px solid"
                        borderColor={phase.color}
                      >
                        "{phase.philosophy}"
                      </Text>

                      <SimpleGrid columns={{ base: 1, md: 2 }} gap={5}>
                        {/* Token Activities */}
                        {phase.tokenActivities.length > 0 && (
                          <Box>
                            <Flex align="center" gap={2} mb={3}>
                              <Icon as={FaCoins} color="yellow.400" boxSize={4} />
                              <Text fontWeight="700" fontSize="sm" color={textPrimary}>
                                Token Activities
                              </Text>
                            </Flex>
                            <VStack align="start" spacing={1}>
                              {phase.tokenActivities.map((activity, i) => (
                                <Flex key={i} align="center" justify="space-between" w="full">
                                  <Text fontSize="xs" color={textSecondary}>
                                    {activity.name}
                                  </Text>
                                  <Badge colorScheme="yellow" fontSize="xs" ml={2} flexShrink={0}>
                                    +{activity.tokens} token{activity.tokens > 1 ? "s" : ""}
                                  </Badge>
                                </Flex>
                              ))}
                            </VStack>
                          </Box>
                        )}

                        {/* Courses */}
                        {phase.courses.length > 0 && (
                          <Box>
                            <Flex align="center" gap={2} mb={3}>
                              <Icon as={FaGraduationCap} color="blue.400" boxSize={4} />
                              <Text fontWeight="700" fontSize="sm" color={textPrimary}>
                                Courses
                              </Text>
                            </Flex>
                            <VStack align="start" spacing={2}>
                              {phase.courses.map((course, i) => (
                                <Box key={i}>
                                  <Text fontSize="xs" color={textSecondary} fontWeight="600">
                                    {course.name}
                                  </Text>
                                  {course.gated && (
                                    <Text fontSize="xs" color={phase.color}>
                                      Gate: {course.gated}
                                    </Text>
                                  )}
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        )}

                        {/* Events */}
                        {phase.events.length > 0 && (
                          <Box>
                            <Flex align="center" gap={2} mb={3}>
                              <Icon as={FaCalendarAlt} color="pink.400" boxSize={4} />
                              <Text fontWeight="700" fontSize="sm" color={textPrimary}>
                                Physical Events
                              </Text>
                            </Flex>
                            <VStack align="start" spacing={2}>
                              {phase.events.map((event, i) => (
                                <Flex key={i} align="center" justify="space-between" w="full">
                                  <Text fontSize="xs" color={textSecondary}>
                                    {event.name}
                                  </Text>
                                  <Badge fontSize="xs" variant="outline" colorScheme="pink" ml={2} flexShrink={0}>
                                    {event.frequency}
                                  </Badge>
                                </Flex>
                              ))}
                            </VStack>
                          </Box>
                        )}

                        {/* Tech Milestones */}
                        <Box>
                          <Flex align="center" gap={2} mb={3}>
                            <Icon as={FaLaptopCode} color="purple.400" boxSize={4} />
                            <Text fontWeight="700" fontSize="sm" color={textPrimary}>
                              Technology
                            </Text>
                          </Flex>
                          <VStack align="start" spacing={1}>
                            {phase.techMilestones.map((milestone, i) => (
                              <Flex key={i} align="center" gap={2}>
                                <Icon as={FaCheckCircle} color={`${phase.color}`} boxSize={3} />
                                <Text fontSize="xs" color={textSecondary}>
                                  {milestone}
                                </Text>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                      </SimpleGrid>

                      {/* Revenue + Exit Criteria */}
                      <Box mt={5} bg={subtleBg} borderRadius="lg" p={4}>
                        <Flex align="center" gap={2} mb={2}>
                          <Icon as={FaChartLine} color="green.400" boxSize={4} />
                          <Text fontWeight="700" fontSize="sm" color={textPrimary}>
                            Revenue Target: {phase.revenueMilestone}
                          </Text>
                        </Flex>
                        {phase.exitCriteria.length > 0 && (
                          <>
                            <Text fontSize="xs" fontWeight="600" color={textSecondary} mb={1}>
                              Exit Criteria to Next Phase:
                            </Text>
                            <List spacing={1}>
                              {phase.exitCriteria.map((criteria, i) => (
                                <ListItem key={i} fontSize="xs" color={textSecondary}>
                                  <ListIcon
                                    as={phase.status === "completed" ? FaCheckCircle : FaCircle}
                                    color={phase.status === "completed" ? "green.400" : "gray.400"}
                                    boxSize={2}
                                  />
                                  {criteria}
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </Box>
                    </AccordionPanel>
                  </Card>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>

          {/* The Anna Story */}
          <Card
            bg={cardGradientBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="2xl"
          >
            <CardBody p={8}>
              <Flex align="center" gap={3} mb={4}>
                <Flex
                  align="center"
                  justify="center"
                  w={12}
                  h={12}
                  borderRadius="full"
                  bg="pink.50"
                >
                  <Icon as={FaPaintBrush} w={6} h={6} color="pink.400" />
                </Flex>
                <Box>
                  <Heading size="md" color={textPrimary}>
                    Meet Anna
                  </Heading>
                  <Text fontSize="sm" color="pink.400" fontWeight="600">
                    Teacher. Artist. Community Builder.
                  </Text>
                </Box>
              </Flex>

              <VStack align="start" spacing={3}>
                <Text fontSize="sm" color={textSecondary} lineHeight="1.8">
                  Anna taught for 15 years and creates children's books and art. She discovered Business Builder through a webinar and subscribed for $97/month to run her creative business. Over 3 months, she earned 18 tokens through webinar attendance, social media posts, completing courses, and mentoring two new subscribers.
                </Text>
                <Text fontSize="sm" color={textSecondary} lineHeight="1.8">
                  Tom noticed her consistent contribution and invited her to the first BB Builder Camp. Anna's perspective as an educator brought a unique voice to governance discussions — she proposed rewarding activities related to children's education and creative workshops. The council approved.
                </Text>
                <Text fontSize="sm" color={textSecondary} lineHeight="1.8" fontWeight="600">
                  In the Conscious Economy, Anna isn't just a user. She's a governor, a teacher, and a creator — rewarded for the value she brings to the community, not by stale, archaic, outdated capital structures designed to inherently empower the old rulers of the world. That just doesn't serve us.
                </Text>
              </VStack>

              <Flex mt={5} gap={3} flexWrap="wrap">
                {[
                  { icon: FaGraduationCap, label: "Educators", color: "blue" },
                  { icon: FaPaintBrush, label: "Artists", color: "pink" },
                  { icon: FaChild, label: "Parents", color: "green" },
                  { icon: FaBuilding, label: "Business Owners", color: "purple" },
                  { icon: FaHandshake, label: "Coaches", color: "orange" },
                  { icon: FaShieldAlt, label: "Healthcare", color: "teal" },
                ].map((tag, i) => (
                  <Tag key={i} size="md" colorScheme={tag.color} borderRadius="full" variant="subtle">
                    <TagLeftIcon as={tag.icon} />
                    <TagLabel fontSize="xs">{tag.label}</TagLabel>
                  </Tag>
                ))}
              </Flex>
            </CardBody>
          </Card>

          {/* Expression of Interest CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)"
            p={10}
            borderRadius="2xl"
            color="white"
          >
            <Heading size="xl" mb={4}>
              Ready to Join the Conscious Economy?
            </Heading>
            <Text fontSize="lg" mb={3} maxW="600px" mx="auto">
              Whether you're an educator, artist, business owner, parent, or community builder — there's a place for you here.
            </Text>
            <Text fontSize="md" mb={6} opacity={0.9} fontStyle="italic">
              Tell us what you care about. We'll show you how you can contribute.
            </Text>
            <Button
              size="lg"
              bg="white"
              color="green.700"
              fontWeight="700"
              px={10}
              py={7}
              fontSize="lg"
              borderRadius="full"
              boxShadow="0 8px 24px rgba(0,0,0,0.2)"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
                bg: "gray.50",
              }}
              onClick={() => navigate("/businessbuilder/interest")}
              rightIcon={<FaArrowRight />}
            >
              Express Your Interest
            </Button>
          </Box>

          {/* How It All Connects */}
          <Box>
            <Heading size="lg" color={textPrimary} mb={4} textAlign="center">
              How It All Connects
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              {[
                {
                  title: "Webinar System",
                  desc: "Attend free webinars, earn tokens, discover what BB can do for your business",
                  color: "#F59E0B",
                  link: "/live-event/interest",
                },
                {
                  title: "Token Economics",
                  desc: "1 BB Token = AU$50. Earn through contribution. Apply as discount on your subscription.",
                  color: "#9333EA",
                  link: "/businessbuilder/tokenomics",
                },
                {
                  title: "Governance",
                  desc: "Community-driven decisions. Quadratic voting. Real meetups. Background-checked members.",
                  color: "#10B981",
                  link: "/businessbuilder/governance",
                },
              ].map((item, i) => (
                <Card
                  key={i}
                  bg={cardGradientBg}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  borderRadius="xl"
                  cursor="pointer"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-4px)", borderColor: item.color }}
                  onClick={() => navigate(item.link)}
                >
                  <CardBody p={5}>
                    <Box w={2} h={2} borderRadius="full" bg={item.color} mb={3} />
                    <Heading size="sm" color={textPrimary} mb={2}>
                      {item.title}
                    </Heading>
                    <Text fontSize="xs" color={textSecondary} lineHeight="1.6">
                      {item.desc}
                    </Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* Bottom quote */}
          <Box textAlign="center" py={4}>
            <Text fontSize="md" color={textSecondary} fontStyle="italic">
              "Today's subscriber is tomorrow's governor. Today's governor is tomorrow's sovereign."
            </Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Journey;
